/**
 * Convex + React Hooks
 *
 * This file provides React hooks for Convex real-time features (messages, notifications).
 * Uses the @pronteer/convex package for all Convex imports.
 *
 * Usage:
 * const conversations = useConversations();
 * const { mutate: sendMessage } = useSendMessage();
 */

import { useQuery, useMutation } from '@pronteer/convex/react'
import { api as convex } from '@pronteer/convex/api'
import type { Id } from '@pronteer/convex/dataModel'

/**
 * Conversation Hooks
 */

// Get all conversations for the current user
export function useConversations(
  companyId: string | undefined,
  crewId: string | undefined,
  archived?: boolean,
) {
  return useQuery(
    convex.conversations.list,
    companyId && crewId ? { companyId, crewId, archived } : 'skip'
  )
}

// Get a single conversation by ID
export function useConversation(conversationId: Id<'conversations'> | undefined, crewId: string | undefined) {
  return useQuery(
    convex.conversations.get,
    conversationId && crewId ? { conversationId, crewId } : 'skip'
  )
}

// Find conversation between specific users
export function useFindConversationByParticipants(companyId: string | undefined, crewIds: string[]) {
  return useQuery(
    convex.conversations.findByParticipants,
    companyId && crewIds.length > 0 ? { companyId, crewIds } : 'skip'
  )
}

// Create a new conversation
export function useCreateConversation() {
  return useMutation(convex.conversations.create)
}

// Add participant to conversation
export function useAddParticipant() {
  return useMutation(convex.conversations.addParticipant)
}

// Remove participant from conversation
export function useRemoveParticipant() {
  return useMutation(convex.conversations.removeParticipant)
}

// Mark conversation as read
export function useMarkConversationAsRead() {
  return useMutation(convex.conversations.markAsRead)
}

// Toggle mute status
export function useToggleMute() {
  return useMutation(convex.conversations.toggleMute)
}

// Toggle pin status
export function useTogglePin() {
  return useMutation(convex.conversations.togglePin)
}

// Delete conversation
export function useDeleteConversation() {
  return useMutation(convex.conversations.deleteConversation)
}

// Set conversation archive status
export function useSetConversationArchived() {
  return useMutation(convex.conversations.setArchived)
}

/**
 * Message Hooks
 */

// Get all messages in a conversation (oldest first)
export function useMessages(conversationId: Id<'conversations'> | undefined, crewId: string | undefined) {
  return useQuery(
    convex.messages.listAll,
    conversationId && crewId ? { conversationId, crewId } : 'skip'
  )
}

// Get a single message
export function useMessage(messageId: Id<'messages'> | undefined, crewId: string | undefined) {
  return useQuery(
    convex.messages.get,
    messageId && crewId ? { messageId, crewId } : 'skip'
  )
}

// Search messages in a conversation
export function useSearchMessages(
  conversationId: Id<'conversations'> | undefined,
  searchTerm: string,
  crewId: string | undefined,
  limit?: number
) {
  return useQuery(
    convex.messages.search,
    conversationId && searchTerm && crewId
      ? { conversationId, searchTerm, crewId, limit }
      : 'skip'
  )
}

// Send a message
export function useSendMessage() {
  return useMutation(convex.messages.send)
}

// Mark message as read
export function useMarkMessageAsRead() {
  return useMutation(convex.messages.markAsRead)
}

// Delete a message
export function useDeleteMessage() {
  return useMutation(convex.messages.deleteMessage)
}

// Edit a message
export function useEditMessage() {
  return useMutation(convex.messages.edit)
}

export function useGenerateUploadUrl() {
  return useMutation(convex.messages.generateUploadUrl)
}

export function useGenerateGetUrl() {
  return useMutation(convex.messages.generateGetUrl)
}
/**
 * Notification Hooks
 */

// Get all notifications for the current user (pass archived to list archived ones)
export function useNotifications(
  crewId: string | undefined,
  limit?: number,
  archived?: boolean,
) {
  return useQuery(
    convex.notifications.list,
    crewId ? { crewId, limit, archived } : 'skip'
  )
}

// Get total unread chat message count across all conversations
export function useTotalUnreadChatCount(
  companyId: string | undefined,
  crewId: string | undefined,
) {
  return useQuery(
    convex.conversations.getTotalUnreadCount,
    companyId && crewId ? { companyId, crewId } : 'skip',
  )
}

// Get unread chat message count within ARCHIVED conversations (for the Archive dot)
export function useArchivedUnreadChatCount(
  companyId: string | undefined,
  crewId: string | undefined,
) {
  return useQuery(
    convex.conversations.getArchivedUnreadCount,
    companyId && crewId ? { companyId, crewId } : 'skip',
  )
}

// Get a single notification by ID
export function useNotification(notificationId: Id<'notifications'> | undefined, crewId: string | undefined) {
  return useQuery(
    convex.notifications.getById,
    notificationId && crewId ? { notificationId, crewId } : 'skip'
  )
}

// Get unread notification count
export function useUnreadNotificationCount(crewId: string | undefined) {
  return useQuery(
    convex.notifications.getUnreadCount,
    crewId ? { crewId } : 'skip'
  )
}

// Get unread notifications
export function useUnreadNotifications(crewId: string | undefined) {
  return useQuery(
    convex.notifications.getUnread,
    crewId ? { crewId } : 'skip'
  )
}

// Get unread task notifications (category = 'task')
export function useUnreadTaskNotifications(crewId: string | undefined) {
  return useQuery(
    convex.notifications.getUnreadByCategory,
    crewId ? { crewId, category: 'task' } : 'skip'
  )
}

// Create a notification
export function useCreateNotification() {
  return useMutation(convex.notifications.create)
}

// Mark notification as read
export function useMarkNotificationAsRead() {
  return useMutation(convex.notifications.markAsRead)
}

// Mark notification as unread
export function useMarkNotificationAsUnread() {
  return useMutation(convex.notifications.markAsUnread)
}

// Mark all notifications as read
export function useMarkAllNotificationsAsRead() {
  return useMutation(convex.notifications.markAllAsRead)
}

// Archive / unarchive a notification
export function useSetNotificationArchived() {
  return useMutation(convex.notifications.setArchived)
}

// Delete a notification
export function useDeleteNotification() {
  return useMutation(convex.notifications.deleteNotification)
}

// Delete all notifications
export function useDeleteAllNotifications() {
  return useMutation(convex.notifications.deleteAll)
}

// Cleanup expired notifications
export function useCleanupExpiredNotifications() {
  return useMutation(convex.notifications.cleanupExpired)
}

/**
 * Announcement Hooks (reads only — writes go through the API/oRPC, see
 * lib/hooks/announcement.ts)
 */

// Get all announcements with read counts
export function useAnnouncements(companyId: string | undefined, crewId: string | undefined) {
  return useQuery(
    convex.announcements.list,
    companyId && crewId ? { companyId, crewId } : 'skip'
  )
}

// Get a specific announcement by ID
export function useAnnouncement(announcementId: Id<'announcements'> | undefined) {
  return useQuery(
    convex.announcements.getById,
    announcementId ? { id: announcementId } : 'skip'
  )
}

// Mark an announcement as read
export function useMarkAnnouncementAsRead() {
  return useMutation(convex.announcements.markAsRead)
}

/**
 * AI Chat Hooks
 */

// Get all AI chat sessions for current user
export function useAiChatSessions(companyId: string | undefined, crewId: string | undefined) {
  return useQuery(
    convex.aiChat.getSessions,
    companyId && crewId ? { companyId, crewId } : 'skip'
  )
}

// Get a single AI chat session with messages
export function useAiChatSession(sessionId: Id<'aiChatSessions'> | undefined) {
  return useQuery(
    convex.aiChat.getSession,
    sessionId ? { sessionId } : 'skip'
  )
}

// Get AI chat messages for a session
export function useAiChatMessages(sessionId: Id<'aiChatSessions'> | undefined) {
  return useQuery(
    convex.aiChat.getMessages,
    sessionId ? { sessionId } : 'skip'
  )
}

// Create a new AI chat session
export function useCreateAiChatSession() {
  return useMutation(convex.aiChat.createSession)
}

// Update AI chat session title
export function useUpdateAiChatSessionTitle() {
  return useMutation(convex.aiChat.updateSessionTitle)
}

// Archive an AI chat session
export function useArchiveAiChatSession() {
  return useMutation(convex.aiChat.archiveSession)
}

// Delete an AI chat session
export function useDeleteAiChatSession() {
  return useMutation(convex.aiChat.deleteSession)
}

// Send a message to AI chat (creates user message + pending assistant message)
export function useSendAiChatMessage() {
  return useMutation(convex.aiChat.sendMessage)
}

// Update assistant message (used for streaming responses)
export function useUpdateAiAssistantMessage() {
  return useMutation(convex.aiChat.updateAssistantMessage)
}
